import { defineStore } from 'pinia';

// Three-way theme preference. "system" defers to the OS-level `prefers-color-scheme` query
// and stays reactive to it (operator changes their OS appearance → SPA flips). "light" /
// "dark" pin the resolved theme regardless of system setting. Preference persists across
// sessions via localStorage; default for new sessions is "system".
//
// The store applies the resolved theme by toggling the `.p-dark` class on
// `document.documentElement`. That class is what PrimeVue's styled-mode darkModeSelector
// matches (configured in main.ts).
export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'retail-config-server.theme';
const DARK_CLASS = 'p-dark';

interface ThemeState {
  preference: ThemePreference;
  systemDark: boolean;
  initialised: boolean;
}

function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'system' || stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // Ignore: private mode / storage disabled. Default applies.
  }
  return 'system';
}

function writePreference(p: ThemePreference) {
  try {
    window.localStorage.setItem(STORAGE_KEY, p);
  } catch {
    // Same — fall through silently.
  }
}

export const useThemeStore = defineStore('theme', {
  state: (): ThemeState => ({
    preference: 'system',
    systemDark: false,
    initialised: false,
  }),
  getters: {
    // The actual theme being applied right now. Mirrors what's on `document.documentElement`.
    resolvedDark(state): boolean {
      if (state.preference === 'dark') return true;
      if (state.preference === 'light') return false;
      return state.systemDark;
    },
  },
  actions: {
    bootstrap() {
      if (this.initialised) return;
      this.preference = readPreference();

      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      this.systemDark = mq.matches;
      const onChange = (e: MediaQueryListEvent) => {
        this.systemDark = e.matches;
        this.apply();
      };
      // addEventListener is the modern API; older Safari needs addListener as a fallback.
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', onChange);
      } else if (typeof (mq as MediaQueryList & { addListener?: (cb: (e: MediaQueryListEvent) => void) => void }).addListener === 'function') {
        (mq as MediaQueryList & { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener(onChange);
      }

      this.initialised = true;
      this.apply();
    },

    setPreference(p: ThemePreference) {
      this.preference = p;
      writePreference(p);
      this.apply();
    },

    apply() {
      const el = document.documentElement;
      if (this.resolvedDark) {
        el.classList.add(DARK_CLASS);
      } else {
        el.classList.remove(DARK_CLASS);
      }
    },
  },
});
